import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState
} from 'react';

import { Damage } from 'types/damage/damage';

export type DamageContextType = {
  damages: Damage[];
  current: Damage;
  capture: boolean;
  index: number;
  editing: number;
  setCapture: Dispatch<SetStateAction<boolean>>;
  setCurrent: Dispatch<SetStateAction<Damage>>;
  setImage: (image?: string) => void;
  reset: () => void;
  submit: () => void;
  editDamage: (index: number) => void;
  deleteDamage: (index: number) => void;
  resetDamages: () => void;
  restoreDamages: Dispatch<SetStateAction<Damage[]>>;
  setIndex: Dispatch<SetStateAction<number>>;
};

const DamageContext = createContext<DamageContextType>({
  damages: [],
  capture: false,
  current: {},
  index: 0,
  editing: -1,
  setCapture: () => {},
  setCurrent: () => {},
  setImage: () => {},
  reset: () => {},
  submit: () => {},
  editDamage: () => {},
  deleteDamage: () => {},
  resetDamages: () => {},
  restoreDamages: () => {},
  setIndex: () => {}
});

export const useDamage = () => useContext(DamageContext);

export const DamageProvider: FC<PropsWithChildren> = ({ children }) => {
  const [damages, setDamages] = useState<Damage[]>([]);
  const [current, setCurrent] = useState<Damage>({});
  const [capture, setCapture] = useState(false);
  const [editing, setEditing] = useState(-1);
  const [index, setIndex] = useState(0);

  const resetDamages = useCallback(() => {
    setCurrent({});
    setCapture(false);
    setDamages([]);
    setEditing(-1);
    setIndex(0);
  }, [setCurrent, setCapture, setDamages, setEditing, setIndex]);

  const setImage = useCallback((imageBase64?: string) => setCurrent({ ...current, imageBase64 }), [current]);

  const reset = useCallback(() => {
    setCurrent({});
    setEditing(-1);
    setIndex(0);
  }, [setCurrent]);

  const submit = useCallback(() => {
    setDamages(p => {
      const damagesCopy = [...p];
      /* replace damage at editing index or insert new damage */
      damagesCopy.splice(Math.max(editing, 0), Number(editing in damagesCopy), current);
      return damagesCopy;
    });
    setCurrent({});
    setEditing(-1);
  }, [current, editing, setCurrent, setDamages]);

  const editDamage = (index: number) => {
    if (index in damages) {
      setEditing(index);
      setCurrent({ ...damages[index] });
    }
  };

  const deleteDamage = (index: number) => {
    if (index in damages)
      setDamages(p => {
        const damagesCopy = [...p];
        damagesCopy.splice(index, 1);
        return damagesCopy;
      });
  };

  // useEffect(() => console.log(current.image), [current.image]);

  return (
    <DamageContext.Provider
      value={{
        current,
        damages,
        capture,
        index,
        editing,
        editDamage,
        deleteDamage,
        setCapture,
        setCurrent,
        setImage,
        reset,
        submit,
        resetDamages,
        restoreDamages: setDamages,
        setIndex
      }}
    >
      {children}
    </DamageContext.Provider>
  );
};
